import type { QuoteRow } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { MY_DESIGNS_PATH, MY_QUOTES_PATH } from '@/shared/config';
import { formatCount } from '@/shared/lib';
import { OverviewStat } from './overview-stat';

interface OverviewStatsProps {
  /** 내 견적 첫 페이지 — 못 읽었으면 null. */
  readonly quotes: { readonly items: readonly QuoteRow[]; readonly total: number } | null;
  /** 내 디자인 첫 페이지 — 못 읽었으면 null. */
  readonly designs: { readonly count: number; readonly hasMore: boolean } | null;
}

const EMPTY_VALUE = '—';

/**
 * 요약 줄 — 전체 견적(서버 총계), 견적 도착·검토 중(첫 페이지 기준), 저장한 디자인(첫 페이지 기준).
 * 상태별 건수 API 가 없어 첫 페이지에 없는 견적이 있으면 "최근 n건 기준"이라고 밝힌다.
 */
export const OverviewStats = ({ quotes, designs }: OverviewStatsProps): ReactElement => {
  const issued = quotes?.items.filter((row) => row.status === 'ISSUED').length ?? null;
  const inReview =
    quotes?.items.filter((row) => row.status === 'REQUESTED' || row.status === 'REVISION_REQUESTED')
      .length ?? null;
  const isPartial = quotes !== null && quotes.total > quotes.items.length;

  return (
    <section aria-label="요약" className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-px border border-foreground/10 bg-foreground/10 lg:grid-cols-4">
        <OverviewStat
          label="견적 도착"
          value={issued === null ? EMPTY_VALUE : formatCount(issued)}
          to={MY_QUOTES_PATH}
          highlighted={issued !== null && issued > 0}
        />
        <OverviewStat
          label="검토 중인 견적"
          value={inReview === null ? EMPTY_VALUE : formatCount(inReview)}
          to={MY_QUOTES_PATH}
        />
        <OverviewStat
          label="전체 견적"
          value={quotes === null ? EMPTY_VALUE : formatCount(quotes.total)}
          to={MY_QUOTES_PATH}
        />
        <OverviewStat
          label="저장한 디자인"
          value={
            designs === null
              ? EMPTY_VALUE
              : `${formatCount(designs.count)}${designs.hasMore ? '+' : ''}`
          }
          to={MY_DESIGNS_PATH}
        />
      </div>
      {isPartial ? (
        <p className="text-xs text-muted-foreground">
          견적 도착·검토 중은 최근 {formatCount(quotes.items.length)}건 기준입니다.
        </p>
      ) : null}
    </section>
  );
};
